// Wealth-Building Assistant (/dashboard/wealth-assistant).
//
// Streams answers token-by-token from POST /api/wealth-chat/stream (private
// Ollama llama3.2 model). PROTECTED: route wrapped in <ProtectedRoute> and the
// backend endpoint requires the same JWT as every other dashboard API.

import type { ReactNode } from 'react';

import { BookOpen, Info, Lock, Zap } from 'lucide-react';

import { Layout, Sidebar } from '../../shared/ui';
import { WealthChat } from './components/WealthChat';

const InfoCard = ({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) => (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm">
        <div className="flex items-center gap-2">
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-2 text-emerald-600 dark:text-emerald-400">
                {icon}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{title}</div>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">{children}</p>
    </div>
);

export const WealthChatPage = () => (
    <Layout sidebar={<Sidebar />} title="Wealth Assistant">
        <div className="space-y-6 text-slate-900 dark:text-slate-100 pb-12">
            <header className="border-b border-slate-200 dark:border-slate-800 pb-4">
                <h1 className="text-2xl font-black tracking-tight">Hi! I am Your Personal Wealth-Building Assistant</h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                    Your private financial-education companion. Ask me about any investing concept and get a clear,
                    straight-forward explanation <span className="font-semibold text-emerald-600">prepared by Forge Ultra and streamed in real time</span>.
                </p>
            </header>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
                <WealthChat className="h-[calc(100vh-15rem)] min-h-[520px]" />

                <aside className="space-y-4">
                    <InfoCard icon={<BookOpen className="h-4 w-4" />} title="What it does">
                        Explains financial concepts — compounding, asset classes, risk, rates, portfolio construction — in
                        two short paragraphs.
                    </InfoCard>
                    <InfoCard icon={<Lock className="h-4 w-4" />} title="Private by design">
                        Runs on a self-hosted model inside the Forge Ultra infrastructure. Your questions are not sent
                        to third-party AI providers.
                    </InfoCard>
                    <InfoCard icon={<Zap className="h-4 w-4" />} title="Live streaming">
                        Answers appear word by word. Press <span className="font-semibold">Stop</span> at any time to
                        cancel a response.
                    </InfoCard>
                    <p className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <Info className="mt-0.5 h-4 w-4 shrink-0" />
                        Educational content only — not personalised financial, tax or legal advice. Forge Ultra answers can
                        contain mistakes; verify important information.
                    </p>
                </aside>
            </div>
        </div>
    </Layout>
);

export default WealthChatPage;
